import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { createSumCaptcha } from '../../utils/sumCaptcha.js';

/**
 * @param {'local' | 'server'} mode — local valida en cliente; server solo exige selección (valida la API).
 * @param {{ a: number, b: number, options: number[] } | null} serverChallenge — requerido si mode=server
 * @param {() => void} [onRefresh] — en server, recargar reto desde API
 */
export default function SumCaptcha({
  value,
  onChange,
  onValidChange,
  className = '',
  mode = 'local',
  serverChallenge = null,
  onRefresh,
  successMessage,
  size = 'default',
}) {
  const large = size === 'lg';
  const bodyText = large ? 'text-base' : 'text-sm';
  const metaText = large ? 'text-sm' : 'text-xs';
  const optionText = large ? 'text-base' : 'text-sm';
  const [localChallenge, setLocalChallenge] = useState(() => createSumCaptcha());

  const challenge = mode === 'server' && serverChallenge ? serverChallenge : localChallenge;
  const correct = mode === 'server' ? null : localChallenge.correct;

  const selected = value === '' || value === undefined ? null : Number(value);
  const isCorrect =
    mode === 'server' ? selected !== null && Number.isFinite(selected) : selected === correct;

  const optionId = useMemo(() => `captcha-${Math.random().toString(36).slice(2, 9)}`, []);

  useEffect(() => {
    onValidChange?.(isCorrect);
  }, [isCorrect, onValidChange]);

  function refresh() {
    if (mode === 'server') {
      onRefresh?.();
    } else {
      setLocalChallenge(createSumCaptcha());
    }
    onChange('');
    onValidChange?.(false);
  }

  const defaultSuccess =
    mode === 'server'
      ? 'Verificación lista. Ya puedes enviar tu PQRS.'
      : 'Verificación correcta. Ya puedes iniciar sesión.';

  if (mode === 'server' && !serverChallenge) {
    return (
      <div
        className={`rounded-xl border border-dashed border-slate-200 bg-slate-50/90 p-4 ${bodyText} text-slate-500 ${className}`}
      >
        Cargando verificación…
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-slate-200 bg-slate-50/90 p-4 ring-1 ring-slate-100 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className={`flex items-center gap-2 ${bodyText} font-semibold text-slate-700`}>
          <ShieldCheck className={`${large ? 'h-5 w-5' : 'h-4 w-4'} shrink-0 text-accent-lime`} aria-hidden />
          Verificación de seguridad
        </div>
        <button
          type="button"
          onClick={refresh}
          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 ${metaText} font-medium text-slate-500 transition hover:bg-white hover:text-[#0d2b7f]`}
          title="Generar otra operación"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Cambiar
        </button>
      </div>

      <p className={`mt-3 ${bodyText} text-slate-600`}>
        ¿Cuánto es <span className="font-semibold text-[#0d2b7f]">{challenge.a}</span> +{' '}
        <span className="font-semibold text-[#0d2b7f]">{challenge.b}</span>?
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Selecciona el resultado correcto">
        {challenge.options.map((opt) => {
          const id = `${optionId}-${opt}`;
          const checked = selected === opt;
          const showWrong = mode === 'local' && selected !== null && checked && !isCorrect;
          const showOk = checked && isCorrect;
          return (
            <label
              key={opt}
              htmlFor={id}
              className={`flex cursor-pointer items-center justify-center rounded-xl border px-3 py-3 text-center ${optionText} font-semibold transition ${
                showOk
                  ? 'border-[#1acb12] bg-[#1acb12]/10 text-[#0d2b7f] ring-2 ring-[#1acb12]/35'
                  : showWrong
                    ? 'border-red-300 bg-red-50 text-red-800 ring-2 ring-red-200'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-[#1acb12]/40 hover:bg-[#1acb12]/5'
              }`}
            >
              <input
                id={id}
                type="radio"
                name={optionId}
                value={String(opt)}
                checked={checked}
                onChange={() => onChange(String(opt))}
                className="sr-only"
              />
              {opt}
            </label>
          );
        })}
      </div>

      {mode === 'local' && selected !== null && !isCorrect ? (
        <p className={`mt-2 ${metaText} text-red-600`} role="alert">
          Resultado incorrecto. Intenta de nuevo o pulsa «Cambiar» para otra suma.
        </p>
      ) : null}
      {isCorrect ? (
        <p className={`mt-2 ${metaText} font-medium text-[#00A63E]`}>{successMessage ?? defaultSuccess}</p>
      ) : null}
    </div>
  );
}
